/** KNAYA storefront interactions - native Shopify APIs, no dependencies. */
(() => {
  'use strict';

  const K = window.KNAYA || {};
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const root = (K.root || '/').replace(/\/?$/, '/');
  const jsonRoute = (configured, fallback) => `${(configured || `${root}${fallback}`).replace(/\/?$/, '').replace(/\.js$/, '')}.js`;
  const routes = {
    cart: `${root}cart.js`,
    add: jsonRoute(K.cartAddUrl, 'cart/add'),
    change: jsonRoute(K.cartChangeUrl, 'cart/change'),
    update: jsonRoute(K.cartUpdateUrl, 'cart/update'),
    predictive: K.predictiveSearchUrl || `${root}search/suggest`
  };

  let lastFocus = null;
  let toastTimer;
  let cartBusy = false;

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[character]);

  const safeURL = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '#';
    try {
      const normalized = value.startsWith('//') ? `https:${value}` : value;
      const url = new URL(normalized, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch (_) {
      return '#';
    }
  };

  const money = (amount) => {
    const locale = document.documentElement.lang || 'en';
    const code = K.currency || 'USD';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(Number(amount || 0) / 100);
    } catch (_) {
      return `${(Number(amount || 0) / 100).toFixed(2)} ${code}`;
    }
  };

  const toast = (message) => {
    const element = $('#knaya-toast');
    if (!element || !message) return;
    clearTimeout(toastTimer);
    element.textContent = message;
    element.classList.add('is-visible');
    toastTimer = setTimeout(() => element.classList.remove('is-visible'), 3200);
  };

  const overlay = () => $('[data-drawer-overlay]');
  const drawers = () => $$('.side-drawer, .search-panel');

  const closeDrawers = () => {
    drawers().forEach((element) => {
      element.classList.remove('is-open');
      element.setAttribute('aria-hidden', 'true');
    });
    if (overlay()) overlay().hidden = true;
    document.body.classList.remove('drawer-open');
    if (lastFocus?.isConnected) lastFocus.focus();
  };

  const openDrawer = async (id) => {
    lastFocus = document.activeElement;
    closeDrawers();
    const drawer = $(`#${id}`);
    if (!drawer) return;
    if (overlay()) overlay().hidden = false;
    document.body.classList.add('drawer-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.focus();
    if (id === 'knaya-cart-drawer') await refreshCart();
    if (id === 'knaya-search-drawer') setTimeout(() => $('#knaya-search-input')?.focus(), 80);
  };

  async function fetchCart() {
    const response = await fetch(routes.cart, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(K.strings?.cartError || 'Could not load your bag.');
    const cart = await response.json();
    if (cart.currency) K.currency = cart.currency;
    return cart;
  }

  const renderProperties = (properties) => Object.entries(properties || {})
    .filter(([key, value]) => key.charAt(0) !== '_' && value !== '' && value != null)
    .map(([key, value]) => {
      const text = String(value);
      const renderedValue = text.includes('/uploads/')
        ? `<a href="${escapeHTML(safeURL(text))}" target="_blank" rel="noopener">View file</a>`
        : escapeHTML(text);
      return `<p>${escapeHTML(key)}: ${renderedValue}</p>`;
    }).join('');

  const renderUnitPrice = (item) => {
    const measurement = item.unit_price_measurement;
    if (!item.unit_price || !measurement) return '';
    const reference = Number(measurement.reference_value || 1) === 1 ? '' : measurement.reference_value;
    return `<p class="unit-price">${money(item.unit_price)} / ${escapeHTML(reference)}${escapeHTML(measurement.reference_unit || '')}</p>`;
  };

  function drawCart(cart) {
    $$('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; });
    const heading = $('[data-cart-count-title]');
    if (heading) heading.textContent = `(${cart.item_count})`;

    const total = $('#cart-drawer-subtotal');
    if (total) total.textContent = money(cart.total_price);

    const bottom = $('[data-cart-bottom]');
    if (bottom) bottom.hidden = !cart.items?.length;

    const discounts = $('[data-cart-discounts]');
    if (discounts) {
      discounts.innerHTML = (cart.cart_level_discount_applications || []).map((discount) =>
        `<span>${escapeHTML(discount.title)} <strong>-${money(discount.total_allocated_amount)}</strong></span>`
      ).join('');
    }

    const itemsWrap = $('#cart-drawer-items');
    if (!itemsWrap) return;
    if (!cart.items?.length) {
      itemsWrap.innerHTML = `<div class="drawer-empty"><span class="serif">Your bag is waiting.</span><p>Add something you love.</p><a href="${escapeHTML(safeURL(`${root}collections/all`))}" class="underlined-link">SHOP KNAYA</a></div>`;
      return;
    }

    itemsWrap.innerHTML = cart.items.map((item) => {
      const image = item.image
        ? `<img src="${escapeHTML(safeURL(item.image))}" alt="${escapeHTML(item.product_title)}" loading="lazy" width="176" height="220">`
        : '<span class="drawer-line__placeholder serif">KNAYA</span>';
      const options = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name ? `<p>${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>` : '';
      const comparePrice = item.original_line_price > item.final_line_price ? `<s>${money(item.original_line_price)}</s>` : '';
      const lineDiscounts = (item.line_level_discount_allocations || []).map((allocation) => {
        const title = allocation.discount_application?.title || allocation.title || 'Discount';
        return `<p class="line-discount">${escapeHTML(title)} -${money(allocation.amount)}</p>`;
      }).join('');

      return `<article class="drawer-line">
        <a class="drawer-line__image" href="${escapeHTML(safeURL(item.url))}">${image}</a>
        <div class="drawer-line__body">
          <h3><a href="${escapeHTML(safeURL(item.url))}">${escapeHTML(item.product_title)}</a></h3>
          ${options}${sellingPlan}${renderProperties(item.properties)}
          <div class="drawer-line__price"><strong>${money(item.final_line_price)}</strong>${comparePrice}</div>
          ${renderUnitPrice(item)}${lineDiscounts}
          <div class="drawer-qty">
            <button type="button" data-cart-change="${escapeHTML(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button type="button" class="drawer-remove" data-cart-change="${escapeHTML(item.key)}" data-quantity="0">REMOVE</button>
      </article>`;
    }).join('');
  }

  async function refreshCart() {
    try {
      drawCart(await fetchCart());
    } catch (error) {
      console.warn('[KNAYA]', error);
      toast(error.message || K.strings?.cartError);
    }
  }

  async function changeCart(key, quantity, trigger) {
    if (cartBusy) return;
    cartBusy = true;
    trigger?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(routes.change, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || K.strings?.cartError);
      if (data.currency) K.currency = data.currency;
      drawCart(data);
    } catch (error) {
      toast(error.message || K.strings?.cartError);
    } finally {
      cartBusy = false;
      trigger?.removeAttribute('aria-busy');
    }
  }

  async function applyDiscount(container) {
    if (!container || cartBusy) return;
    const input = $('[data-discount-input]', container);
    const message = $('[data-discount-message]', container);
    const code = input?.value.trim();
    if (!code) {
      if (message) message.textContent = 'Enter a discount code.';
      input?.focus();
      return;
    }

    cartBusy = true;
    if (message) message.textContent = 'Applying...';
    try {
      const payload = { discount: code };
      const cartNote = $('#CartNote');
      if (cartNote) payload.note = cartNote.value;
      const response = await fetch(routes.update, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Could not apply this code.');
      const appliedCode = (cart.discount_codes || []).find((discount) => discount.code?.toLowerCase() === code.toLowerCase());
      const appliedTitle = (cart.cart_level_discount_applications || []).some((discount) => discount.title?.toLowerCase() === code.toLowerCase());
      if (appliedCode && appliedCode.applicable === false && !appliedTitle) throw new Error('This discount code is not available for your bag.');
      if (container.hasAttribute('data-refresh-page')) {
        window.location.reload();
        return;
      }
      drawCart(cart);
      if (message) message.textContent = 'Discount applied.';
    } catch (error) {
      if (message) message.textContent = error.message || 'Could not apply this code.';
    } finally {
      cartBusy = false;
    }
  }

  async function addToCart(form) {
    const button = $('[data-add-button], [data-quick-add-button]', form);
    if (button?.disabled) return;
    const previous = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(routes.add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
      await openDrawer('knaya-cart-drawer');
    } catch (error) {
      toast(error.message || 'Unable to add this item.');
      const productMessage = $('[data-product-message]', form);
      if (productMessage) productMessage.textContent = error.message;
    } finally {
      if (button) {
        button.disabled = false;
        button.innerHTML = previous;
        button.removeAttribute('aria-busy');
      }
      const section = form.closest('[data-product-section]');
      if (section) updateVariant(section, false);
    }
  }

  const getVariants = (section) => {
    if (section._knayaVariants) return section._knayaVariants;
    const node = $('[data-product-variants]', section);
    if (!node) return [];
    try {
      section._knayaVariants = JSON.parse(node.textContent);
      return section._knayaVariants;
    } catch (error) {
      console.warn('[KNAYA] Invalid variant data', error);
      return [];
    }
  };

  const optionGroups = (section) => $$('[data-option-index]', section);
  const selectedOptions = (section) => optionGroups(section).map((group) => $('[data-option-input]:checked', group)?.value);

  function renderOptionState(section) {
    optionGroups(section).forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      $$('[data-option-input]', group).forEach((input) => {
        const label = input.nextElementSibling;
        label?.classList.toggle('is-selected', input.checked);
        label?.classList.toggle('is-unavailable', input.disabled);
      });
      const selected = $('[data-option-input]:checked', group);
      const current = $(`[data-selected-option="${index}"]`, section);
      if (current && selected) current.textContent = selected.value;
    });
  }

  function updateOptionAvailability(section, variants) {
    const groups = optionGroups(section);
    groups.forEach((group, index) => {
      let selected = selectedOptions(section);
      const inputs = $$('[data-option-input]', group);
      inputs.forEach((input) => {
        const available = variants.some((variant) => {
          if (!variant.available || String(variant.options[index]) !== input.value) return false;
          for (let previous = 0; previous < index; previous += 1) {
            if (selected[previous] && String(variant.options[previous]) !== selected[previous]) return false;
          }
          return true;
        });
        input.disabled = !available;
      });

      const checked = $('[data-option-input]:checked', group);
      if (!checked || checked.disabled) {
        const replacement = inputs.find((input) => !input.disabled);
        if (replacement) replacement.checked = true;
      }
      selected = selectedOptions(section);
    });
    renderOptionState(section);
  }

  function formatUnitPrice(variant) {
    if (!variant.unit_price || !variant.unit_price_measurement) return '';
    const measurement = variant.unit_price_measurement;
    const reference = Number(measurement.reference_value || 1) === 1 ? '' : measurement.reference_value;
    return `${money(variant.unit_price)} / ${reference}${measurement.reference_unit || ''}`;
  }

  function updateInventory(section, variant) {
    const element = $('[data-product-inventory]', section);
    if (!element) return;
    const threshold = Number(section.dataset.lowStockThreshold || 5);
    let label = 'OUT OF STOCK';
    if (variant?.available) {
      if (variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') label = 'AVAILABLE TO ORDER';
      else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) label = `ONLY ${variant.inventory_quantity} LEFT`;
      else label = 'IN STOCK';
    }
    element.textContent = label;
    element.classList.toggle('is-unavailable', !variant?.available);
  }

  function updateProductMedia(section, mediaId) {
    if (!mediaId) return;
    const mediaItems = $$('[data-media-id]', section);
    const target = mediaItems.find((item) => String(item.dataset.mediaId) === String(mediaId));
    if (!target) return;
    mediaItems.forEach((item) => item.classList.toggle('is-featured', item === target));
  }

  async function updatePickup(section, variant) {
    const container = $('[data-pickup-container]', section);
    if (!container) return;
    if (!variant?.available) {
      container.innerHTML = '';
      container.removeAttribute('data-variant-id');
      return;
    }
    if (container.dataset.variantId === String(variant.id)) return;
    container.dataset.variantId = String(variant.id);
    container.innerHTML = '';
    try {
      const sectionId = container.dataset.pickupSection || 'pickup-availability';
      const response = await fetch(`${root}variants/${variant.id}/?section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) return;
      const text = await response.text();
      if (container.dataset.variantId !== String(variant.id)) return;
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const content = $('[data-pickup-content]', parsed);
      if (content) container.innerHTML = content.innerHTML;
    } catch (error) {
      console.warn('[KNAYA] Pickup availability failed', error);
    }
  }

  function updateVariant(section, syncURL = true) {
    const variants = getVariants(section);
    if (!variants.length) return;
    updateOptionAvailability(section, variants);
    const options = selectedOptions(section);
    const variant = optionGroups(section).length
      ? variants.find((candidate) => candidate.options.every((value, index) => String(value) === options[index]))
      : variants[0];

    const hidden = $('[data-variant-id]', section);
    const price = $('[data-product-price]', section);
    const compare = $('[data-product-compare]', section);
    const soldOut = $('[data-sold-out]', section);
    const buy = $('[data-add-button]', section);
    const label = $('[data-add-label]', section);
    const message = $('[data-product-message]', section);
    const dynamicCheckout = $('[data-dynamic-checkout]', section);
    const skuRow = $('[data-sku-row]', section);
    const sku = $('[data-product-sku]', section);
    const unitPrice = $('[data-unit-price]', section);
    if (message) message.textContent = '';

    if (!variant) {
      if (buy) buy.disabled = true;
      if (label) label.textContent = String(K.strings?.unavailable || 'UNAVAILABLE').toUpperCase();
      if (soldOut) soldOut.hidden = true;
      if (dynamicCheckout) dynamicCheckout.hidden = true;
      if (message) message.textContent = 'This combination is unavailable.';
      if (hidden) hidden.value = '';
      return;
    }

    if (hidden) hidden.value = variant.id;
    if (price) price.textContent = money(variant.price);
    if (compare) {
      compare.hidden = !(variant.compare_at_price > variant.price);
      compare.textContent = compare.hidden ? '' : money(variant.compare_at_price);
    }
    if (soldOut) soldOut.hidden = variant.available;
    if (buy) buy.disabled = !variant.available;
    if (label) label.textContent = String(variant.available ? (K.strings?.addToBag || 'ADD TO BAG') : (K.strings?.soldOut || 'SOLD OUT')).toUpperCase();
    if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;
    if (skuRow) skuRow.hidden = !variant.sku;
    if (sku) sku.textContent = variant.sku || '';
    if (unitPrice) {
      unitPrice.textContent = formatUnitPrice(variant);
      unitPrice.hidden = !unitPrice.textContent;
    }
    updateInventory(section, variant);
    updateProductMedia(section, variant.featured_media_id);
    updatePickup(section, variant);

    if (syncURL) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  async function loadRecommendations(container) {
    if (!container || container.dataset.loaded === 'true' || !container.dataset.url) return;
    container.dataset.loaded = 'true';
    try {
      const response = await fetch(container.dataset.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(String(response.status));
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const replacement = $('[data-product-recommendations]', parsed);
      if (replacement?.innerHTML.trim()) container.innerHTML = replacement.innerHTML;
      else container.remove();
    } catch (error) {
      console.warn('[KNAYA] Product recommendations failed', error);
      container.remove();
    }
  }

  function initializePredictiveSearch(scope) {
    $$('[data-predictive-search]', scope).forEach((component) => {
      if (component.dataset.initialized === 'true') return;
      component.dataset.initialized = 'true';
      const input = $('[data-predictive-input]', component);
      const results = $('[data-predictive-results]', component);
      if (!input || !results) return;
      let timer;
      let controller;

      const close = () => {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      };

      input.addEventListener('input', () => {
        clearTimeout(timer);
        controller?.abort();
        const term = input.value.trim();
        if (term.length < 2) {
          close();
          return;
        }
        timer = setTimeout(async () => {
          controller = new AbortController();
          try {
            const url = new URL(routes.predictive, window.location.origin);
            url.searchParams.set('q', term);
            url.searchParams.set('resources[type]', 'product');
            url.searchParams.set('resources[limit]', '6');
            url.searchParams.set('resources[options][unavailable_products]', 'last');
            url.searchParams.set('section_id', 'predictive-search');
            const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(String(response.status));
            const text = await response.text();
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const content = $('[data-predictive-content]', parsed);
            results.innerHTML = content?.outerHTML || '';
            input.setAttribute('aria-expanded', String(Boolean(content)));
          } catch (error) {
            if (error.name === 'AbortError') return;
            close();
            console.warn('[KNAYA] Predictive search failed', error);
          }
        }, 250);
      });
    });
  }

  function initialize(scope = document) {
    $$('[data-product-section]', scope).forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';
      updateVariant(section, false);
    });
    $$('[data-product-recommendations]', scope).forEach(loadRecommendations);
    initializePredictiveSearch(scope);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-ajax-add]');
    if (!form) return;
    const submitter = event.submitter;
    if (submitter && !submitter.matches('[data-add-button], [data-quick-add-button]')) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-auto-submit]')) {
      event.target.form?.requestSubmit();
      return;
    }
    const input = event.target.closest('[data-option-input]');
    if (!input) return;
    const section = input.closest('[data-product-section]');
    if (!section) return;
    if (input.dataset.productUrl) {
      const next = new URL(input.dataset.productUrl, window.location.origin);
      const current = new URL(section.dataset.productUrl, window.location.origin);
      if (next.pathname !== current.pathname) {
        window.location.assign(next.href);
        return;
      }
    }
    updateVariant(section);
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-open-cart]')) {
      event.preventDefault();
      openDrawer('knaya-cart-drawer');
      return;
    }
    if (event.target.closest('[data-open-search]')) {
      event.preventDefault();
      openDrawer('knaya-search-drawer');
      return;
    }
    if (event.target.closest('[data-close-drawers]') || event.target.closest('[data-drawer-overlay]')) {
      event.preventDefault();
      closeDrawers();
      return;
    }

    const mobile = event.target.closest('[data-mobile-menu]');
    if (mobile) {
      const navigation = $('#mobile-navigation');
      if (navigation) {
        navigation.hidden = !navigation.hidden;
        mobile.setAttribute('aria-expanded', String(!navigation.hidden));
      }
      return;
    }

    const quantityButton = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (quantityButton) {
      const input = $('input[name="quantity"]', quantityButton.closest('.quantity-control'));
      if (input) {
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const delta = quantityButton.hasAttribute('data-qty-plus') ? 1 : -1;
        input.value = Math.min(max, Math.max(min, (Number(input.value) || min) + delta));
      }
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      changeCart(cartChange.dataset.cartChange, Number(cartChange.dataset.quantity), cartChange);
      return;
    }

    const filters = event.target.closest('[data-toggle-filters]');
    if (filters) {
      const target = $('#knaya-filters');
      if (target) {
        target.hidden = !target.hidden;
        filters.setAttribute('aria-expanded', String(!target.hidden));
      }
      return;
    }

    const discountButton = event.target.closest('[data-apply-discount]');
    if (discountButton) {
      event.preventDefault();
      applyDiscount(discountButton.closest('[data-discount-form]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawers();
    if (event.key !== 'Tab') return;
    const dialog = $('.side-drawer.is-open, .search-panel.is-open');
    if (!dialog) return;
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]', dialog).filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  initialize(document);
})();
